import React, { useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

// Simple accessible accordion item (reused pattern from your Terms page)
function AccordionItem({ id, title, children, isOpen, onToggle }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        aria-expanded={isOpen}
        aria-controls={`panel-${id}`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
      >
        <span className="text-[15px] font-medium text-slate-800">{title}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-slate-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        id={`panel-${id}`}
        role="region"
        aria-labelledby={`btn-${id}`}
        className={`px-4 pb-5 text-sm leading-6 text-slate-700 ${
          isOpen ? "block" : "hidden"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export default function ShippingPolicyPage() {
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const fromHash = window.location.hash?.replace("#", "");
    if (fromHash) setOpenId(fromHash);
  }, []);

  return (
    <main className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <h1 className="text-2xl font-semibold text-slate-900">Shipping & Delivery Policy</h1>
          <p className="mt-1 text-sm text-slate-600">Last updated on Nov 25 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="space-y-3">
          <AccordionItem
            id="overview"
            title="Overview"
            isOpen={openId === null || openId === "overview"}
            onToggle={() => setOpenId(openId === "overview" ? null : "overview")}
          >
            <p>
              For International buyers, orders are shipped and delivered through registered
              international courier companies and/or International speed post only. For
              domestic buyers, orders are shipped through registered domestic courier
              companies and/or speed post only.
            </p>
          </AccordionItem>

          <AccordionItem
            id="timing"
            title="Shipping timeframes"
            isOpen={openId === "timing"}
            onToggle={() => setOpenId(openId === "timing" ? null : "timing")}
          >
            <p>
              Orders are shipped within 0-7 days or as per the delivery date agreed at the
              time of order confirmation and delivery of the shipment subject to Courier
              Company / Post Office norms. ShowTimeBro is not liable for any delay in delivery by the courier company or
              postal authorities and only guarantees to hand over the consignment to the
              courier company or postal authorities within 0-7 days from the date of the
              order and payment or as per the agreed delivery date.
            </p>
          </AccordionItem>

          <AccordionItem
            id="delivery"
            title="Delivery & address"
            isOpen={openId === "delivery"}
            onToggle={() => setOpenId(openId === "delivery" ? null : "delivery")}
          >
            <p>
              Delivery of all orders will be to the address provided by the buyer. Please
              ensure the shipping address, contact number and any special delivery
              instructions are correct at the time of checkout. ShowTimeBro is not
              responsible for deliveries to incorrect or incomplete addresses provided by the
              buyer.
            </p>
          </AccordionItem>

          <AccordionItem
            id="payments-refunds"
            title="Payments, failed transactions & refunds"
            isOpen={openId === "payments-refunds"}
            onToggle={() => setOpenId(openId === "payments-refunds" ? null : "payments-refunds")}
          >
            <p>
              Payment processing is handled by the selected payment gateway. In case of
              failed transactions with successful debits, refunds (if any) are issued by the
              payment provider as per their timelines. For queries related to payment or
              refunds, contact our support (details below).
            </p>
          </AccordionItem>

          <AccordionItem
            id="limitations"
            title="Limitations of liability"
            isOpen={openId === "limitations"}
            onToggle={() => setOpenId(openId === "limitations" ? null : "limitations")}
          >
            <p>
              ShowTimeBro endeavours to deliver orders on time but is not responsible for
              delays caused by courier companies, customs clearance, force majeure events,
              or other third-party issues. Our liability for delayed or lost shipments is
              limited to assisting with claims and communication with the courier provider.
            </p>
          </AccordionItem>

          <AccordionItem
            id="contact"
            title="Contact & helpdesk"
            isOpen={openId === "contact"}
            onToggle={() => setOpenId(openId === "contact" ? null : "contact")}
          >
            <p>
              If you have questions about shipping or need help with an order, please
              contact ShowTimeBro:
            </p>
            <ul className="mt-2 list-disc pl-6 space-y-1">
              <li>
                <span className="font-medium">Email:</span>{" "}
                <a className="text-amber-600 underline" href="mailto:info@showtimebro.com">info@showtimebro.com</a>
              </li>
              <li>
                <span className="font-medium">Phone / WhatsApp:</span> 7440550105
              </li>
            </ul>
          </AccordionItem>
        </div>

        <p className="mt-8 text-xs text-slate-500">
          Note: This Shipping & Delivery Policy is a general policy for orders placed via
          ShowTimeBro. Individual sellers, organisers, or venues may have additional
          shipping terms which will be communicated at checkout where applicable.
        </p>
      </div>
    </main>
  );
}
